'use client'

import { RefreshCwIcon, Trash2Icon } from 'lucide-react'
import { nanoid } from 'nanoid'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useConfirm } from '@/hooks/use-confirm'
import { MAX_COIN_FOR_GIFT, MIN_COIN_FOR_GIFT } from '@/lib/constant'
import type { User, UserCoin, Workspace, WorkspaceCoin } from '@/lib/types'
import { row } from '../ui/_ui-fixed'

interface Props {
  user: User
  onClose: () => void
  onSuccess: () => void
}

export default function GiftCoinModal({ user, onClose, onSuccess }: Props) {
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [target, setTarget] = useState<'user' | 'workspace'>('user')
  const [workspaceId, setWorkspaceId] = useState<string>('')
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false)
  const [coinData, setCoinData] = useState<{
    userCoins: UserCoin[]
    workspaceCoins: WorkspaceCoin[]
  } | null>(null)
  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<{ coin: string }>({
    mode: 'onChange',
  })
  const t = useTranslations('modals.gift-coin')
  const { confirm, isOpen: isConfirmOpen } = useConfirm()

  const fetchCoinData = useCallback(async () => {
    try {
      setRefreshing(true)
      const res = await fetch(
        `/api/dashboard/get-user-coin-breakdown?userId=${user._id.toString()}`,
      )
      if (res.ok) {
        const response = await res.json()
        if (response.success && response.data) {
          setCoinData({
            userCoins: response.data.userCoins || [],
            workspaceCoins: response.data.workspaceCoins || [],
          })
        }
      }
    } catch (error) {
      console.error('Failed to fetch coin data:', error)
    } finally {
      setRefreshing(false)
    }
  }, [user._id])

  const fetchWorkspaces = useCallback(async () => {
    try {
      setLoadingWorkspaces(true)
      const res = await fetch(
        `/api/dashboard/get-user-workspaces?userId=${user._id.toString()}`,
      )
      if (res.ok) {
        const response = await res.json()
        if (response.success && response.data) {
          setWorkspaces(response.data)
          // Auto-select first workspace if available
          if (response.data.length > 0) {
            setWorkspaceId(response.data[0]._id.toString())
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error)
      toast.error('Failed to load workspaces')
    } finally {
      setLoadingWorkspaces(false)
    }
  }, [user._id])

  useEffect(() => {
    fetchCoinData()
    fetchWorkspaces()
  }, [fetchCoinData, fetchWorkspaces])

  const onSubmit = async (data: { coin: string }) => {
    try {
      // Validate workspace selection when target is workspace
      if (target === 'workspace' && !workspaceId) {
        toast.error('Please select a workspace')
        return
      }

      setLoading(true)
      const body: {
        userId: string
        coin: number
        target: 'user' | 'workspace'
        workspaceId?: string
      } = {
        userId: user._id.toString(),
        coin: Number.parseInt(data.coin, 10),
        target,
      }

      // Include workspaceId only when target is workspace
      if (target === 'workspace') {
        body.workspaceId = workspaceId
      }

      const res = await fetch('/api/dashboard/gift-coin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Failed to gift coin')
      toast.success(t('success'))
      onSuccess()
      onClose()
    } catch (_error) {
      toast.error(t('error'))
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num)
  }

  const formatDate = (date: string | Date | number) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleDeleteCoin = async (
    e: React.MouseEvent,
    coinId: string,
    coinType: 'user' | 'workspace',
  ) => {
    e.preventDefault()
    e.stopPropagation()

    const isConfirmed = await confirm({
      title: 'Delete Coin',
      description: `Are you sure you want to delete this ${coinType} coin? This action cannot be undone.`,
      okText: 'Delete',
      cancelText: 'Cancel',
    })

    if (!isConfirmed) {
      return
    }

    try {
      const res = await fetch('/api/dashboard/delete-coin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coinId,
          coinType,
          userId: user._id.toString(),
        }),
      })

      if (!res.ok) throw new Error('Failed to delete coin')

      toast.success('Coin deleted successfully')
      await fetchCoinData() // Refresh coin data
    } catch (error) {
      toast.error('Failed to delete coin')
      console.error('Delete coin error:', error)
    }
  }

  return (
    <DialogContent
      className="max-w-2xl max-h-[90vh] overflow-scroll"
      onInteractOutside={(e) => {
        // Prevent closing when confirmation dialog is open
        if (isConfirmOpen) {
          e.preventDefault()
        }
      }}
      onEscapeKeyDown={(e) => {
        // Prevent closing when confirmation dialog is open
        if (isConfirmOpen) {
          e.preventDefault()
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>{t('title')}</DialogTitle>
        <div className="grid text-sm mt-2">
          {row('User ID', user._id.toString())}
          {row('Email', user.email)}
          {row('Total Coin', formatNumber(user.coin))}
        </div>
        <DialogDescription>
          You should gift new user workspace coins. User Coins will be
          deprecated in the future. Old users with project subscriptions will
          use user coins. Although the logic now falls back to user_coins, we
          should respect the new pricing model on workspace.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Coin Breakdown Section */}
        <div id="coin-breakdown" className="space-y-4 max-h-96 overflow-scroll">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Coin Breakdown</h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                return fetchCoinData()
              }}
              disabled={refreshing}
            >
              <RefreshCwIcon
                className={`size-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          </div>

          {/* Workspace Coins */}
          <div className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">Workspace Coins</span>
              <span className="text-lg font-bold">
                {coinData
                  ? formatNumber(
                      coinData.workspaceCoins.reduce(
                        (sum, c) => sum + c.remaining,
                        0,
                      ),
                    )
                  : '...'}
              </span>
            </div>
            {coinData && coinData.workspaceCoins.length > 0 && (
              <div className="space-y-2 mt-2">
                {coinData.workspaceCoins.map((coin) => {
                  return (
                    <div
                      key={coin._id}
                      className="text-sm bg-muted/50 rounded p-2 space-y-1 relative"
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          return handleDeleteCoin(
                            e,
                            coin._id.toString(),
                            'workspace',
                          )
                        }}
                        className="absolute top-2 right-2 p-1 hover:bg-destructive/10 rounded transition-colors"
                        title="Delete coin"
                      >
                        <Trash2Icon className="size-3 text-destructive" />
                      </button>
                      <div className="flex justify-between items-center pr-8">
                        <span className="font-medium capitalize">
                          {coin.type}{' '}
                          <span className="text-muted-foreground">
                            ({coin.tier})
                          </span>
                        </span>
                        <span className="font-mono">
                          {formatNumber(coin.remaining)}/
                          {formatNumber(coin.amount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Created: {formatDate(coin.create_time)}</span>
                        <span>
                          {coin.expiration_at
                            ? `Expires: ${formatDate(coin.expiration_at)}`
                            : 'No expiration'}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {coinData && coinData.workspaceCoins.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">
                No workspace coins
              </p>
            )}
          </div>

          {/* User Coins */}
          <div className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">User Coins</span>
              <span className="text-lg font-bold">
                {coinData
                  ? formatNumber(
                      coinData.userCoins.reduce(
                        (sum, c) => sum + c.remaining,
                        0,
                      ),
                    )
                  : '...'}
              </span>
            </div>
            {coinData && coinData.userCoins.length > 0 && (
              <div className="space-y-2 mt-2">
                {coinData.userCoins.map((coin) => {
                  return (
                    <div
                      key={coin._id}
                      className="text-sm bg-muted/50 rounded p-2 space-y-1 relative"
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          return handleDeleteCoin(
                            e,
                            coin._id.toString(),
                            'user',
                          )
                        }}
                        className="absolute top-2 right-2 p-1 hover:bg-destructive/10 rounded transition-colors"
                        title="Delete coin"
                      >
                        <Trash2Icon className="size-3 text-destructive" />
                      </button>
                      <div className="flex justify-between items-center pr-8">
                        <span className="font-medium capitalize">
                          {coin.type}{' '}
                          <span className="text-muted-foreground">
                            ({coin.tier})
                          </span>
                        </span>
                        <span className="font-mono">
                          {formatNumber(coin.remaining)}/
                          {formatNumber(coin.amount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Created: {formatDate(coin.create_time)}</span>
                        <span>
                          {coin.expiration_at
                            ? `Expires: ${formatDate(coin.expiration_at)}`
                            : 'No expiration'}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {coinData && coinData.userCoins.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">
                No user coins
              </p>
            )}
          </div>
        </div>

        {/* Gift Form Section */}
        <div className="space-y-4 pt-4 border-t">
          <div className="space-y-2">
            <Label>Target</Label>
            <RadioGroup
              value={target}
              onValueChange={(value) => {
                return setTarget(value as 'user' | 'workspace')
              }}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="user" id="user" />
                <Label htmlFor="user" className="cursor-pointer">
                  User Coins
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="workspace" id="workspace" />
                <Label htmlFor="workspace" className="cursor-pointer">
                  Workspace Coins
                </Label>
              </div>
            </RadioGroup>
          </div>

          {target === 'workspace' && (
            <div className="space-y-2">
              <Label htmlFor="workspace-select">Select Workspace *</Label>
              {loadingWorkspaces ? (
                <div className="text-sm text-muted-foreground">
                  Loading workspaces...
                </div>
              ) : workspaces.length === 0 ? (
                <div className="text-sm text-destructive">
                  No workspaces found for this user
                </div>
              ) : (
                <Select value={workspaceId} onValueChange={setWorkspaceId}>
                  <SelectTrigger id="workspace-select">
                    <SelectValue placeholder="Select a workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map((workspace) => {
                      return (
                        <SelectItem
                          key={workspace._id.toString()}
                          value={workspace._id.toString()}
                        >
                          {workspace.name || 'Unnamed Workspace'}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="coin">{t('gift_amount')}</Label>
            <Input
              id="coin"
              type="number"
              min={MIN_COIN_FOR_GIFT}
              max={MAX_COIN_FOR_GIFT}
              required
              placeholder="Enter amount..."
              {...register('coin', {
                required: true,
                min: MIN_COIN_FOR_GIFT,
                max: MAX_COIN_FOR_GIFT,
              })}
            />
          </div>

          <Button
            type="submit"
            variant={'active'}
            disabled={
              loading ||
              !isValid ||
              (target === 'workspace' && !workspaceId) ||
              (target === 'workspace' && workspaces.length === 0)
            }
            className="w-full"
          >
            {loading ? t('sending') : t('send')}
          </Button>
        </div>
      </form>
    </DialogContent>
  )
}
